// src/components/DerinBaglantiDurumu.tsx
//
// EMİR #94.4 — derin bağlantı çözümlenemediğinde gösterilen durum.
//
// ESKİ DAVRANIŞ: `replaceState("/")` — kullanıcı ana sayfada buluyordu kendini,
// NEDEN olduğunu asla öğrenmiyordu ve URL silindiği için yenileyip tekrar
// deneme imkânı da kalmıyordu. Paylaşım/büyüme yolunun tam ortası.
//
// 🔒 İKİ DURUM, ÜÇ GERÇEKLİK (mahremiyet kararı — bkz. deepLinkResolve.ts):
//   "yok"         → içerik gerçekten yok VEYA görme iznin yok. İKİSİ AYNI
//                   görünür; ayrışması gizli hesabın/engelin varlığını
//                   sızdırırdı. Birleşmesi TASARIM, kusur değil.
//   "ulasilamadi" → ağ/sunucu durumu. İçerik hakkında bilgi sızdırmaz, bu
//                   yüzden AYRI gösterilir ve "Tekrar dene" sunar.
//
// 🧪 TEST KANCASI: `data-vw-derin` — kâşif invariant'ı bu ekranı DOM'dan tanır
// ve iki durumun AYRIŞMADIĞINI (yok ≡ izin-yok) doğrular. İz olmadan araç bu
// ekranı göremez (maskeleme sınıfının dersi).
//
// 🔒 STATİK IMPORT: ağ yokken gösterilecek bir ekranın chunk'ını o an ağdan
// indirmeye çalışmak, hatayı hatayla karşılamaktır (#94.3 kalıcı kuralı) —
// bu yüzden appModals'ta lazy DEĞİL, doğrudan import edilir.

import React from "react";

interface Props {
  durum: "yok" | "ulasilamadi";
  /**
   * NE bulunamadı — İÇERİK mi (varsayılan) yoksa ROTA mı?
   *
   * ⚠️ `durum` DEĞİL, ONA EK: `data-vw-derin` değeri (`yok`/`ulasilamadi`)
   * BİLEREK değişmedi. O iz sanal dünyanın invariant'larında, `sometimes`
   * sayaçlarında ve GÖZ sahnelerinde (59, 67) okunuyor; üçüncü bir değer
   * eklemek, ürünle ilgisi olmayan bir kapsam kaymasıyla o araçların
   * hepsini aynı anda kör ederdi. Ayrım ek bir izle (`data-vw-derin-tur`)
   * taşınır — mevcut hiçbir seçici (`[data-vw-derin]`, `[data-vw-derin='yok']`)
   * bundan etkilenmez.
   *
   * Neden ayrı metin: ölü bir rotada ("/settings") *"Bu içerik kaldırılmış
   * olabilir"* demek yanlış bir teşhistir — kaldırılan bir içerik yoktur,
   * o adres hiçbir zaman bir yere gitmemiştir.
   */
  tur?: "icerik" | "rota";
  onRetry?: () => void;
  onClose?: () => void;
  retrying?: boolean;
}

export default function DerinBaglantiDurumu({ durum, tur = "icerik", onRetry, onClose, retrying }: Props) {
  const ulasilamadi = durum === "ulasilamadi";
  // Rota hâli YALNIZ "yok" ile anlamlıdır: "ulaşılamadı" bir ağ durumudur ve
  // rota tanımıyla ilgisi yoktur (çözücü ağa hiç çıkmaz).
  const rotaYok = !ulasilamadi && tur === "rota";
  return (
    <div
      data-vw-derin={durum}
      data-vw-derin-tur={rotaYok ? "rota" : "icerik"}
      role="alert"
      style={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 14,
        padding: 24,
        textAlign: "center",
        background: "var(--bg, #ffffff)",
        color: "var(--text, #262626)",
      }}
    >
      <div style={{ fontSize: 40, lineHeight: 1 }} aria-hidden="true">
        {ulasilamadi ? "⚡" : "🔍"}
      </div>
      <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>
        {ulasilamadi ? "Bağlantı kurulamadı" : rotaYok ? "Sayfa bulunamadı" : "İçerik bulunamadı"}
      </h2>
      <p style={{ fontSize: 14, opacity: 0.75, margin: 0, maxWidth: 320 }}>
        {ulasilamadi
          ? "Bu içeriğe şu anda ulaşamıyoruz. İnternet bağlantını kontrol edip tekrar dene — bağlantı adresi duruyor."
          : rotaYok
            ? "Bu adres MyLASA'da bir sayfaya karşılık gelmiyor."
            : "Bu içerik kaldırılmış olabilir ya da artık kullanılamıyor."}
      </p>
      <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
        {ulasilamadi && (
          <button
            type="button"
            className="pressable"
            onClick={onRetry}
            disabled={!!retrying}
            aria-busy={retrying ? "true" : "false"}
            data-vw-derin-retry={retrying ? "1" : "0"}
            style={{
              padding: "10px 22px", borderRadius: 10, border: "none",
              fontSize: 15, fontWeight: 600,
              background: retrying ? "#7EC4F5" : "#0095F6", color: "#fff",
              cursor: retrying ? "default" : "pointer",
            }}
          >
            {retrying ? "Deniyor…" : "Tekrar dene"}
          </button>
        )}
        <button
          type="button"
          className="pressable"
          onClick={onClose}
          style={{
            padding: "10px 22px", borderRadius: 10,
            border: "1px solid rgba(128,128,128,0.35)",
            fontSize: 15, fontWeight: 600, background: "transparent",
            color: "inherit", cursor: "pointer",
          }}
        >
          Ana sayfaya dön
        </button>
      </div>
    </div>
  );
}
